import React, { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { m } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import NotFoundPage from './NotFoundPage';
import { SITE_ORIGIN, internalUrl, tradesNicheUrl } from './siteConfig';
import { findNichePage, resolveNicheArea } from './tradeNicheUtils';
import { getAreaBySlug } from './localAreasData';
import {
  DEFAULT_META_DESCRIPTION,
  DEFAULT_PAGE_TITLE,
  getMetaDescriptionEl,
  setCanonical
} from './seoDefaults';
import { tradesLocalBusinessAggregateRating } from './localAreaPageSeo';
import { ORGANIZATION_SCHEMA_ID, organizationSchemaNode } from './organizationSchema';
import { tradesNicheClickyTitle, tradesNicheMetaDescription } from './tradesPageSeo';
import FreeSeoAuditCta from './FreeSeoAuditCta';

function TradesNichePage() {
  const { tradeSlug, areaSlug } = useParams();
  const niche = findNichePage(tradeSlug, areaSlug);
  const area = niche ? resolveNicheArea(niche.areaSlug) : null;
  const valid = Boolean(niche && area);
  const locationsMatch = valid ? getAreaBySlug(niche.areaSlug) : null;

  const canonical = valid ? tradesNicheUrl(niche.tradeSlug, niche.areaSlug) : `${SITE_ORIGIN}/`;
  const schemaId = valid ? `webcodecraft-trades-niche-${niche.tradeSlug}-${niche.areaSlug}` : '';

  const jsonLd = useMemo(() => {
    if (!valid) return null;
    const pageTitle = tradesNicheClickyTitle(niche, area);
    const desc = tradesNicheMetaDescription(niche, area);
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        organizationSchemaNode(),
        {
          '@type': 'WebPage',
          '@id': `${canonical}#webpage`,
          url: canonical,
          name: pageTitle,
          description: desc,
          isPartOf: { '@type': 'WebSite', name: 'WebCodeCraft', url: SITE_ORIGIN },
          inLanguage: 'en-GB'
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
            { '@type': 'ListItem', position: 2, name: 'Trades & builders', item: `${SITE_ORIGIN}/trades` },
            {
              '@type': 'ListItem',
              position: 3,
              name: `${niche.tradeLabel} in ${area.name}`,
              item: canonical
            }
          ]
        },
        {
          '@type': 'LocalBusiness',
          '@id': `${canonical}#localbusiness`,
          name: `WebCodeCraft ${area.name}`,
          url: canonical,
          parentOrganization: { '@id': ORGANIZATION_SCHEMA_ID },
          aggregateRating: tradesLocalBusinessAggregateRating(`${canonical}#aggregateRating`),
          areaServed: {
            '@type': 'Place',
            name: area.name,
            containedInPlace: {
              '@type': 'AdministrativeArea',
              name: area.region,
              containedInPlace: { '@type': 'Country', name: 'United Kingdom' }
            }
          }
        }
      ]
    });
  }, [valid, niche, area, canonical]);

  useEffect(() => {
    if (!valid) return undefined;

    const title = tradesNicheClickyTitle(niche, area);
    const description = tradesNicheMetaDescription(niche, area);
    document.title = title;
    const meta = getMetaDescriptionEl();
    const prevDesc = meta?.getAttribute('content') || '';
    meta?.setAttribute('content', description);
    setCanonical(canonical);

    const existing = document.getElementById(schemaId);
    if (!existing && jsonLd) {
      const el = document.createElement('script');
      el.id = schemaId;
      el.type = 'application/ld+json';
      el.textContent = jsonLd;
      document.head.appendChild(el);
    }

    return () => {
      document.title = DEFAULT_PAGE_TITLE;
      meta?.setAttribute('content', prevDesc || DEFAULT_META_DESCRIPTION);
      setCanonical(`${SITE_ORIGIN}/`);
      const script = document.getElementById(schemaId);
      if (script) script.remove();
    };
  }, [valid, niche, area, canonical, jsonLd, schemaId]);

  if (!valid) {
    return <NotFoundPage />;
  }

  const tradeLower = niche.tradeLabel.toLowerCase();

  return (
    <>
      <Header />
      <main
        id="main-content"
        className="pt-24 pb-16 min-w-0 overflow-x-clip bg-gradient-to-b from-gray-50 to-white min-h-screen"
      >
        <article className="container mx-auto px-4 max-w-3xl">
          <m.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <p className="text-sm text-indigo-600 font-medium mb-4">
              <Link to="/" className="hover:underline">
                Home
              </Link>
              <span className="mx-2 text-gray-400" aria-hidden="true">
                /
              </span>
              <Link to="/trades" className="hover:underline">
                Trades &amp; builders
              </Link>
              <span className="mx-2 text-gray-400" aria-hidden="true">
                /
              </span>
              <span className="text-gray-600">
                {niche.tradeLabel} in {area.name}
              </span>
            </p>

            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Web design &amp; development for {niche.tradeLabel} in {area.name}
            </h1>
            <p className="text-lg font-medium text-indigo-800 mb-6 leading-snug">
              £0 upfront, low monthly care—bespoke sites with custom code, not WordPress, for {tradeLower} in{' '}
              {area.name}. HTTPS, clear quote routes, no massive upfront invoice.
            </p>

            <FreeSeoAuditCta className="mb-8" />

            <div className="space-y-5 text-gray-700 leading-relaxed">
              <p>
                {niche.p1 ? (
                  niche.p1
                ) : (
                  <>
                    Homeowners and businesses searching for {tradeLower} in {area.name} expect fast pages, trust signals,
                    and easy contact. WebCodeCraft builds bespoke sites for trades: services, coverage areas, reviews, and
                    accreditation blocks where they help—so you look as sharp online as on site.
                  </>
                )}
              </p>
              <p>
                {niche.p2 ? (
                  niche.p2
                ) : (
                  <>
                    Whether you run emergency call-outs or staged renovation work, we keep stacks patched and hosted with UK
                    appropriate options and technical SEO foundations suited to local search in {area.region}.
                  </>
                )}
              </p>
            </div>

            {locationsMatch && (
              <p className="text-gray-700 leading-relaxed mt-6">
                For broader local SEO context on {locationsMatch.name}, see our{' '}
                <Link to={`/locations/${locationsMatch.slug}`} className="text-indigo-600 font-medium hover:underline">
                  {locationsMatch.name} locations page
                </Link>{' '}
                and the full{' '}
                <Link to="/locations" className="text-indigo-600 font-medium hover:underline">
                  UK locations hub
                </Link>
                .
              </p>
            )}

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-3">What you get on published tiers</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Bespoke layouts and custom builds—not off-the-shelf WordPress themes</li>
              <li>SSL, backup-minded hosting workflows, and security-oriented maintenance per plan</li>
              <li>Structured enquiry or quote routes instead of a buried email address</li>
              <li>Semantic pages and metadata aimed at local visibility for {area.name}</li>
            </ul>

            <p className="mt-8 text-gray-700 leading-relaxed">
              Compare packages on our{' '}
              <a href={internalUrl('services')} className="text-indigo-600 font-medium hover:underline">
                services &amp; pricing
              </a>{' '}
              page, read the{' '}
              <a href={internalUrl('faq')} className="text-indigo-600 font-medium hover:underline">
                FAQ
              </a>
              , or browse all{' '}
              <Link to="/trades" className="text-indigo-600 font-medium hover:underline">
                trades city pages
              </Link>{' '}
              and{' '}
              <Link to="/locations" className="text-indigo-600 font-medium hover:underline">
                wider area pages
              </Link>
              .
            </p>

            <div className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-indigo-600 to-cyan-600 text-white">
              <h3 className="text-xl font-bold mb-2">
                Quote for {niche.tradeLabel} in {area.name}
              </h3>
              <p className="mb-4 opacity-95">Describe your trade, coverage, and goals—we reply with next steps.</p>
              <a
                href={internalUrl('contact')}
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-indigo-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors"
              >
                Contact WebCodeCraft
              </a>
            </div>
          </m.div>
        </article>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
}

export default TradesNichePage;
